// BuildingDefaults_Pure.ts — the PURE, Node-testable fallback-merge engine for the Buildings Set1
// modifier system (§3.3). NO mobx-graphlink / three / DOM imports here, so the Node vitest lane can
// import it directly (importing mobx-graphlink pulls @apollo/client, which is broken under Node 22 —
// see vitest.config.ts). The `CreateAccessor` wrappers in `BuildingAccessors.ts` feed the DB-derived
// chain into this pure engine.
//
// The whole point of the group structure (§2.2) is that the merge is ONE generic routine: walk the
// leaf tree of `ResolvedBuildingDefaults`, and for each leaf pick the FIRST non-null value across an
// ordered (nearest-first) list of sources. Provenance is the source label of the winning leaf, which
// powers the UI's "inherited from X" hints + "reset to inherited" buttons.
import {
	HARDCODED_BUILDING_DEFAULTS,
	type ResolvedBuildingDefaults,
} from "../@MapObject/ObjModifiers/Building/BuildingDefaults_Hardcoded.js";
import {BuildingDefaults} from "../@MapObject/ObjModifiers/Mod_Building.js";
import type {ObjModifier} from "../@MapObject/ObjModifier.js";

/** Deep-nullable variant of a shape: every object key becomes optional and every leaf may also be
 *  `n` (null = "inherit from the next source"). The fallback sources are the `BuildingDefaults`
 *  classes whose leaves are already nullable, so `DeepNullable<BuildingDefaults>` is the natural
 *  source-shape type — `Mod_Room`, `Mod_Building.defaults`, `BuildingMaterials`, etc. all satisfy
 *  it. */
export type DeepNullable<T> = {[K in keyof T]?: T[K] extends object ? DeepNullable<T[K]> | n : T[K] | n};

/** One fallback-chain entry. `label` is a human-readable provenance tag ("Level (Floor 3)", …).
 *  `defaults` is a partial, deep-nullable `BuildingDefaults` — exactly the shape the modifier
 *  classes (`Mod_Room`, `Mod_Building.defaults`, …) satisfy. */
export type BuildingDefaultsSource = {label: string; defaults: Partial<DeepNullable<BuildingDefaults>>};

// ---------------------------------------------------------------------------
// Leaf enumeration over the resolved shape
// ---------------------------------------------------------------------------
const LEAF_PATHS: string[] = [];
(function collectLeafPaths(template: unknown, prefix: string, out: string[]): void {
	if (template == null || typeof template != "object") { out.push(prefix); return; }
	for (const key of Object.keys(template as object)) {
		collectLeafPaths((template as any)[key], prefix ? `${prefix}.${key}` : key, out);
	}
})(HARDCODED_BUILDING_DEFAULTS, "", LEAF_PATHS);

function getPath(obj: unknown, path: string): unknown {
	let cur: any = obj;
	for (const part of path.split(".")) {
		if (cur == null) return undefined;
		cur = cur[part];
	}
	return cur;
}
function setPath(obj: Record<string, any>, path: string, value: unknown): void {
	const parts = path.split(".");
	let cur = obj;
	for (let i = 0; i < parts.length - 1; i++) { cur = cur[parts[i]] ??= {}; }
	cur[parts[parts.length - 1]] = value;
}

/** Deep per-leaf first-non-null merge over an ordered source list. Returns a fully-populated
 *  (no-nulls-remaining) `ResolvedBuildingDefaults`, plus per-leaf provenance labels.
 *  `HARDCODED_BUILDING_DEFAULTS` is the guaranteed final source, so every leaf resolves. */
export function MergeBuildingDefaultsChain(sources: BuildingDefaultsSource[]): {
	values: ResolvedBuildingDefaults;
	provenance: {[leafPath: string]: string};
} {
	const values: Record<string, any> = {};
	const provenance: {[leafPath: string]: string} = {};
	for (const path of LEAF_PATHS) {
		let resolved: unknown;
		let resolvedLabel = "hardcoded";
		for (const src of sources) {
			const v = getPath(src.defaults, path);
			if (v != null) { resolved = v; resolvedLabel = src.label; break; }
		}
		if (resolved == null) resolved = getPath(HARDCODED_BUILDING_DEFAULTS, path); // safety net
		setPath(values, path, resolved);
		provenance[path] = resolvedLabel;
	}
	return {values: values as ResolvedBuildingDefaults, provenance};
}

// ---------------------------------------------------------------------------
// Self-modifier → defaults-source mapping
// ---------------------------------------------------------------------------
/** Map a single `ObjModifier` wrapper's building modifier (Room/Level/Window/Door) into the
 *  `ResolvedBuildingDefaults` shape, so the self-mod participates in the same generic merge.
 *  Returns null when the wrapper carries no mapped building modifier. A `Mod_Building` wrapper is
 *  NOT mapped here — its grouped defaults are pulled in via `FindOwningBuilding` instead. */
export function MapModifierToDefaultsSource(mod: ObjModifier): BuildingDefaultsSource|null {
	if (!mod.enabled) return null;
	if (mod.room) {
		const m = mod.room;
		return {label: "Room", defaults: {
			room: {wallThickness: m.wallThickness, hasFloor: m.hasFloor, hasCeiling: m.hasCeiling},
			level: {floorThickness: m.floorThickness, wallHeight: m.wallHeight},
			materials: m.materials,
		}};
	}
	if (mod.level) {
		const m = mod.level;
		return {label: "Level", defaults: {
			level: {floorThickness: m.floorThickness, wallHeight: m.wallHeight},
			// Set2 §4.1: a Level's `wallThickness` is a source for the SINGLE `room.wallThickness`
			// leaf (it sits between the room's own override and the building default), so a whole
			// floor's walls can be retuned in one place.
			room: {wallThickness: m.wallThickness},
			materials: m.materials,
		}};
	}
	if (mod.window) {
		const m = mod.window;
		return {label: "Window", defaults: {
			window: {
				width: m.width, height: m.height, sillHeight: m.sillHeight,
				paneDivisionsX: m.paneDivisionsX, paneDivisionsY: m.paneDivisionsY,
			},
			materials: m.materials,
		}};
	}
	if (mod.door) {
		const m = mod.door;
		return {label: "Door", defaults: {
			door: {
				width: m.width, height: m.height, swing: m.swing, openAngle: m.openAngle,
			},
			materials: m.materials,
		}};
	}
	if (mod.opening) {
		const m = mod.opening;
		return {label: "Opening", defaults: {
			opening: {
				width: m.width,
			},
			materials: m.materials,
		}};
	}
	return null;
}
